import clsx from 'clsx';

import ClickoutWrapper from 'components/ClickoutWrapper';
import CtaButton from 'components/CtaButton';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Listing from 'components/Listing';
import Paper from 'components/Paper';
import Rating from 'components/Rating';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import { revealSEOUrl } from 'helpers/SEOHelper';
import openUrl from 'helpers/openUrl';
import { getengagementDestination } from 'helpers/reviews';
import useViewport from 'hooks/useViewport';

import StyleOnScroll from '../StyleOnScroll';
import CloseButton from './components/CloseButton';

import classes from './ListingTopHeader.module.scss';

const SCROLL_THRESHOLD = 212;

/** @type {import("./types").default} */
const ListingTopHeader = ({
  scrollTargetRef,
  shouldRenderCloseButton,
  numberOfColumns,
  listing,
  listingPosition,
}) => {
  const { addEvent, sendEvent } = useEventContext({
    engagementComponent: 'product_main-card',
  });
  const { up, down } = useViewport();
  const { isCypressTakingSnapshots } = useAppContext();
  const mainCtaUrl = listing.is_premium
    ? listing.cta_urls.cta
    : listing.cta_urls.detail || listing.cta_urls.cta;

  return (
    <Paper className={clsx('ListingTopHeader', classes.paper)} elevation={0}>
      <div
        className={clsx(classes.container, {
          [classes.narrow]: numberOfColumns >= 3,
        })}
      >
        <div className={classes.listingInfo}>
          <Link
            className={classes.logoWrapper}
            data-testid="listing-top-header-logo"
            href={listing.cta_urls.detail}
            {...addEvent({
              engagementDestination: 'go-to_product_details',
              engagementDetails: 'logo_product',
              name: 'engagement_product_click',
            })}
          >
            <img
              className={classes.logo}
              src={`${listing.logo_url}?w=60`}
              alt={`${listing.name} logo`}
            />
          </Link>
          <div className={classes.infoWrapper}>
            {down('sm') && (
              <StyleOnScroll
                className={isCypressTakingSnapshots ? '' : classes.showLogo}
                onScrollClassName={
                  isCypressTakingSnapshots ? '' : classes.hiddenLogo
                }
                containerRef={scrollTargetRef}
                scrollThreshold={SCROLL_THRESHOLD}
              >
                <Link
                  className={classes.logoMobile}
                  data-testid="listing-top-header-logo-mobile"
                  href={listing.cta_urls.detail}
                  {...addEvent({
                    engagementDestination: 'go-to_product_details',
                    engagementDetails: 'logo_product',
                    name: 'engagement_product_click',
                  })}
                >
                  <img
                    className={classes.logo}
                    src={`${listing.logo_url}?w=60`}
                    alt={`${listing.name} logo`}
                  />
                </Link>
              </StyleOnScroll>
            )}
            <Link
              href={listing.cta_urls.detail}
              {...addEvent({
                engagementDestination: 'go-to_product_details',
                engagementDetails: 'text-link_product-name',
                name: 'engagement_product_click',
              })}
            >
              <h2
                className={clsx(classes.title, {
                  [classes.small]: numberOfColumns > 2,
                })}
              >
                {listing.name}
              </h2>
            </Link>
            <div className={classes.ratingContainer}>
              <div className={classes.rating}>
                <Rating.Link
                  href={listing.cta_urls.reviews}
                  average={listing.rating_avg}
                  totals={listing.num_reviews}
                  ratings={listing.reviews?.by_rating}
                  ratingsHrefs={listing.reviews?.by_rating_urls}
                  onClick={(e) => e.stopPropagation()}
                  {...addEvent({
                    engagementDestination: getengagementDestination(
                      listing.cta_urls.reviews
                    ),
                    engagementDetails: 'text-link_number-reviews',
                    name: 'engagement_product_click',
                  })}
                />
              </div>
            </div>
          </div>
          {shouldRenderCloseButton && (
            <>
              {down('sm') && (
                <StyleOnScroll
                  className={classes.closeButtonWrapper}
                  onScrollClassName={classes.closeButtonWrapperMobileTablet}
                  target={scrollTargetRef}
                  scrollThreshold={SCROLL_THRESHOLD}
                >
                  <CloseButton
                    onClick={() => {
                      openUrl(
                        revealSEOUrl(listing.cta_urls.remove_comparison.url),
                        { target: '_self' }
                      );
                      sendEvent({
                        engagementDestination: 'compare_remove-product',
                        engagementDetails: 'icon_remove',
                        name: 'engagement_cta_compare',
                      });
                    }}
                  />
                </StyleOnScroll>
              )}
              <div className={classes.closeButtonWrapperDesktop}>
                <CloseButton
                  onClick={() => {
                    openUrl(
                      revealSEOUrl(listing.cta_urls.remove_comparison.url),
                      { target: '_self' }
                    );
                    sendEvent({
                      engagementDestination: 'compare_remove-product',
                      engagementDetails: 'icon_remove',
                      name: 'engagement_cta_compare',
                    });
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div className={classes.buttonWrapper}>
          <ClickoutWrapper
            url={mainCtaUrl}
            isClickout={listing.is_premium}
            buyerIntentProductId={listing.global_product_id}
            buyerIntentSourceLocation="compare/listing-top-header:main-cta"
            buyerIntentTrackAdClick={listing.is_premium}
            className={classes.ctaButtonWrapper}
            listingPosition={listingPosition}
            {...addEvent({
              productClickPosition: listingPosition,
              engagementDestination: listing.is_premium
                ? 'go-to_vendor-website'
                : 'go-to_product_details',
              engagementDetails: listing.is_premium
                ? 'button_visit-website'
                : 'button_learn-more',
              name: listing.is_premium
                ? 'engagement_product_clickout'
                : 'engagement_product_click',
            })}
          >
            <CtaButton
              fullWidth={true}
              label={
                listing.is_premium ? (
                  <>
                    visit website
                    <Icon
                      icon={['far', 'external-link']}
                      size="16"
                      className={classes.ctaIcon}
                    />
                  </>
                ) : (
                  'learn more'
                )
              }
            />
          </ClickoutWrapper>
          <Listing.Buttons.Save
            listing={listing}
            onlyIcon={up('md')}
            className={classes.save}
          />
        </div>
      </div>
    </Paper>
  );
};

export default ListingTopHeader;
